import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Trash2, Archive } from 'lucide-react';

/**
 * Compact "Sent / Saved Quotes" timeline for an appraisal — lists every
 * archived PDF and lets the user re-download the exact bytes that were
 * saved at the time. Hidden when there are no archives so a brand-new
 * appraisal's Overview tab stays clean.
 */
export default function SentQuotesPanel({ api, appraisalId, refreshKey = 0, isAdmin = false }) {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/quote-archives', { params: { appraisal_id: appraisalId } });
      // Hide soft-deleted rows from the timeline.
      setArchives((Array.isArray(data) ? data : []).filter(a => !a.deleted));
    } catch { setArchives([]); }
    setLoading(false);
  }, [api, appraisalId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const download = async (archive) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/quote-archives/${archive._id}/download`;
    try {
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) throw new Error('Download failed');
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = archive.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (e) { alert('Download failed: ' + e.message); }
  };

  const remove = async (id) => {
    try { await api.delete(`/quote-archives/${id}`); setDeleting(null); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return null;
  if (archives.length === 0) return null;

  const fmtDate = (d) => {
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + dt.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
    } catch { return String(d).slice(0, 16); }
  };

  return (
    <div data-testid="sent-quotes-panel" className="bg-white border border-[#E2E2DF] rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
          <Archive size={14} className="text-[#D35400]" />
          Sent / Saved Quotes
          <span className="text-[10px] font-mono-num text-slate-400 font-normal">({archives.length})</span>
        </h3>
      </div>
      <div className="space-y-1.5">
        {archives.map(a => (
          <div key={a._id} data-testid={`archive-row-${a._id}`} className="border border-[#E2E2DF] rounded-md px-3 py-2 flex items-center gap-3 hover:bg-slate-50/60 group">
            <FileText size={16} className="text-slate-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate" title={a.filename}>{a.filename}</p>
              <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                <span className="font-mono-num">{(a.size_bytes / 1024).toFixed(0)} KB</span>
                <span className="text-slate-300">·</span>
                <span>{fmtDate(a.created_at)}</span>
                {a.saved_by_email && <><span className="text-slate-300">·</span><span className="truncate max-w-[180px]">{a.saved_by_email}</span></>}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] ${a.report_type === 'quotation-detailed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-orange-50 text-[#A04000] border border-orange-100'}`}>{a.report_type === 'quotation-detailed' ? 'Detailed' : 'Basic'}</span>
              </p>
              {a.note && <p className="text-[11px] text-slate-500 italic mt-0.5 truncate" title={a.note}>"{a.note}"</p>}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                data-testid={`archive-download-${a._id}`}
                onClick={() => download(a)}
                className="flex items-center gap-1 px-2.5 py-1.5 bg-white border border-[#E2E2DF] hover:border-[#D35400] hover:text-[#D35400] rounded-md text-xs font-semibold text-slate-700 transition-colors"
              >
                <Download size={12} /> Download
              </button>
              {isAdmin && (
                deleting === a._id ? (
                  <>
                    <button data-testid={`archive-confirm-delete-${a._id}`} onClick={() => remove(a._id)} className="px-2 py-1 bg-red-500 text-white rounded text-xs">Delete</button>
                    <button onClick={() => setDeleting(null)} className="px-2 py-1 border border-slate-300 rounded text-xs text-slate-500">Cancel</button>
                  </>
                ) : (
                  <button data-testid={`archive-delete-${a._id}`} onClick={() => setDeleting(a._id)} className="p-1.5 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Delete archive">
                    <Trash2 size={13} />
                  </button>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
